/* eslint-disable max-lines */
import React, { Suspense, lazy, useCallback, FC, HTMLAttributes, useRef, useState, useEffect } from 'react';
import { Redirect } from '@okapi/router-react';
import { PdpLegalDisclaimer } from 'features/legal-disclaimer';
import { PdpBreadcrumps } from 'features/pdp-breadcrumps';
import { PdpNavigation } from 'features/page-navigation';
import { Sections } from 'features/page-navigation/components/section-refs-context';
import { Characteristics } from 'features/product-info/components/characteristics';
import { PdpD9nSection } from 'features/product-description/components';
import { PdpProductReviews } from 'features/product-reviews';
import { PdpBottomD9nSectionState } from 'features/product-description/components/pdp-bottom-d9n-section';
import { PdpCollectionGoods, PdpSuggestionsGoods } from 'features/related-products';
import { PdpPageWarningMessage } from 'features/page-warning-message';
import { Layout } from 'fronton-react';
import { useBreakpoints } from 'common/hooks';
import { ProductMediaContentConnected } from 'features/product-info/components/pdp-showcase/components/product-media-content';
import { ProductInfoContentConnected } from 'features/product-info/components/pdp-showcase/components';
import { Manuals } from 'features/manuals';
import { useWindowSize } from '@navi-toolbox/hooks';
import { cx } from 'linaria';
import { useRRPageView } from 'features/retail-rocket/hooks/use-rr-page-view';
import {
  productInformationContainer,
  productInfoDesktopPlusStyle,
  pageContent,
  paddingReset,
  descriptionContent,
  navigation,
  Containers,
  orderedContentNotSellable,
  Styles,
  sectionDivider,
  setDivider,
} from './styles';
import { moveContent } from './utils/move-content';
import { PageContentMappedState } from './types';
import { useSelector } from 'react-redux';
import { getBreadcrumbs } from 'features/page-model';
import { getLinkedPagesLevel5 } from 'features/linked-pages/ducks/selectors';
import { LinkedPages } from 'features/linked-pages';
import { OtherProducts } from 'features/other-products';
import { isProductSet } from 'features/product-info/ducks/selectors';

const LazySubstitutesProducts = lazy(() => import('features/related-products/components/substitute-goods'));
const LazyPdpComplementGoods = lazy(() => import('features/related-products/components/complement-goods'));

export const DATA_QA_PDP_SUBSTITUTE_GOODS = 'pdp-substitute-goods';

const descriptionRef = React.createRef<HTMLElement | null>();
const characteristicsRef = React.createRef<HTMLElement | null>();
const collectionGoods = React.createRef<HTMLElement | null>();
const reviewsRef = React.createRef<HTMLElement | null>();
const substituteGoods = React.createRef<HTMLElement | null>();
const complementGoods = React.createRef<HTMLElement | null>();

const refs: Sections.ContextType = new Map([
  ['description', descriptionRef],
  ['characteristics', characteristicsRef],
  ['collectionProducts', collectionGoods],
  ['complements', complementGoods],
  ['substitutes', substituteGoods],
  ['reviews', reviewsRef],
]);

type PageContentProps = PageContentMappedState & HTMLAttributes<HTMLElement>;

export const PageContent: FC<PageContentProps> = ({
  seoProductDescription,
  productId,
  quantity,
  isAvailableForPurchase,
  regionId,
  isRedirectTo404,
  ...htmlProps
}) => {
  const movementRef = useRef<HTMLDivElement>(null);
  const [complementBlockIsUp, setComplementBlockIsUp] = useState<boolean>(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [newQuantity, setNewQuantity] = useState(0);
  const isSet = useSelector(isProductSet);
  const { width } = useWindowSize();
  const { mobile, smMobile } = useBreakpoints() || {};
  const onChangeCb = useCallback(
    (cancelAnimations = false) => {
      if (isAvailableForPurchase) {
        moveContent({
          movementRef,
          complementsRef: refs.get('complements'),
          newQuantity,
          setComplementBlockIsUp,
          isFirstTime: cancelAnimations ? false : isFirstTime,
          setIsFirstTime,
          cancelAnimations,
        });
      }
    },
    [isAvailableForPurchase, isFirstTime, newQuantity],
  );

  useEffect(() => {
    setNewQuantity(quantity);
  }, [quantity]);
  useEffect(() => {
    setIsFirstTime(true);
  }, []);
  useEffect(() => {
    onChangeCb();
  }, [
    newQuantity,
    complementBlockIsUp,
    isFirstTime,
    isAvailableForPurchase,
    mobile,
    smMobile,
    width,
    onChangeCb,
  ]);
  const { desktop, largeDesktop } = useBreakpoints() || {
    desktop: false,
    largeDesktop: false,
  };
  const shouldDivideProductInfoContent = desktop || largeDesktop;

  useRRPageView(productId, regionId);

  const breadcrumbs = useSelector(getBreadcrumbs) || [];
  const linkedPages = useSelector(getLinkedPagesLevel5);

  if (isRedirectTo404) {
    return <Redirect to="/404" />;
  }

  return (
    <>
      <Layout>
        <PdpBreadcrumps className={paddingReset} data={breadcrumbs} />
        <PdpPageWarningMessage />
        <div className={productInformationContainer}>
          <div className={pageContent} data-qa="content-container" {...htmlProps}>
            <ProductMediaContentConnected />
            {!shouldDivideProductInfoContent && <ProductInfoContentConnected />}
            <Sections.Context.Provider value={refs}>
              <PdpNavigation className={navigation} />
              <div className={isAvailableForPurchase ? descriptionContent : orderedContentNotSellable}>
                <Containers.ComplementMovementBlocks id="description-container" ref={movementRef}>
                  <PdpD9nSection
                    onCollapsedToggle={() => onChangeCb(true)}
                    className={cx(
                      Styles.pdpD9nSectionContainer,
                      complementBlockIsUp ? sectionDivider : undefined,
                    )}
                  />
                  <Characteristics
                    className={cx(
                      Styles.pdpSectionInsideInfoBlockStyle,
                      refs.get('description')?.current || complementBlockIsUp ? sectionDivider : undefined,
                    )}
                    onCollapsedToggle={() => onChangeCb(true)}
                  />

                  <PdpLegalDisclaimer className={Styles.pdpSectionInsideInfoBlockStyle} />
                  <Manuals />

                  {isSet ? (
                    <div className={setDivider} />
                  ) : (
                    <>
                      <OtherProducts className={cx(Styles.pdpSectionInsideInfoBlockStyle, sectionDivider)} />

                      {isAvailableForPurchase && (
                        <PdpCollectionGoods
                          className={cx(Styles.pdpSectionInsideInfoBlockStyle, sectionDivider)}
                          id="collectionProducts"
                        />
                      )}
                      <PdpSuggestionsGoods productId={productId} />
                      {!isAvailableForPurchase && (
                        <PdpCollectionGoods
                          className={cx(Styles.pdpSectionInsideInfoBlockStyle, sectionDivider)}
                          id="collectionProducts"
                        />
                      )}
                    </>
                  )}
                </Containers.ComplementMovementBlocks>
                {!isSet && (
                  <>
                    <div>
                      <Suspense fallback="...Загрузка">
                        <LazyPdpComplementGoods
                          className={cx(
                            Styles.pdpSectionInsideInfoBlockStyle,
                            !complementBlockIsUp ? sectionDivider : undefined,
                          )}
                          productId={productId}
                        />
                      </Suspense>
                      <Suspense fallback="...Загрузка">
                        <LazySubstitutesProducts
                          className={sectionDivider}
                          productId={productId}
                          data-qa={DATA_QA_PDP_SUBSTITUTE_GOODS}
                          id="substitutes"
                        />
                      </Suspense>
                    </div>
                    <PdpProductReviews id="reviews" className={sectionDivider} />
                  </>
                )}
              </div>
            </Sections.Context.Provider>
          </div>
          {shouldDivideProductInfoContent && (
            <div>
              <ProductInfoContentConnected className={productInfoDesktopPlusStyle} />
            </div>
          )}
        </div>

        {!isSet && linkedPages && linkedPages.length > 0 && <LinkedPages pages={linkedPages} />}
      </Layout>
      <PdpBottomD9nSectionState />
    </>
  );
};
